import { useEffect, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { X, RefreshCw } from 'lucide-react';
import TimeRangeSelector from './TimeRangeSelector';
import { fetchSingleMetricHistory } from '../../api/api';
import { parseMetricValue } from '../../fonctions/fonctions';
import './MetricPanel.css';

const ALERTE_LABELS = { 1: 'Normal', 2: 'Sous surveillance', 3: 'Anomalie', 4: 'Critique' };

function parseNumeric(raw) {
  if (!raw) return null;
  const { percent } = parseMetricValue(raw);
  if (percent !== null) return percent;
  const m = raw.match(/^([\d.]+)/);
  return m ? parseFloat(m[1]) : null;
}

function alerteColor(level) {
  if (level === 4) return '#dc2626';
  if (level === 3) return '#ea580c';
  if (level === 2) return '#d97706';
  return '#16a34a';
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const color = alerteColor(payload[0]?.payload?.alerte);
  return (
    <div style={{
      background: 'white', border: '1px solid #e5e7eb',
      borderRadius: 8, padding: '7px 12px', fontSize: '0.78rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ color: '#9ca3af', marginBottom: 3 }}>
        {new Date(label).toLocaleString('fr-FR')}
      </div>
      <div style={{ color, fontWeight: 700 }}>{payload[0]?.payload?.rawValue}</div>
    </div>
  );
}

function MetricPanel({ open, server, metric, onClose }) {
  const [visible,  setVisible]  = useState(false);
  const [hours,    setHours]    = useState(24);
  const [points,   setPoints]   = useState([]);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (open) requestAnimationFrame(() => setVisible(true));
    else       setVisible(false);
  }, [open]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!open || !server || !metric) return;
    setLoading(true);
    fetchSingleMetricHistory(server.serveur_id, metric.metric_name, hours)
      .then(r => { if (r.success) setPoints(r.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, server, metric, hours]);

  if (!open && !visible) return null;

  const title      = metric?.intitule || metric?.metric_name || 'Métrique';
  const serverName = server?.serveur_name || '';
  const alertLevel = parseInt(metric?.alerte, 10) || 1;
  const color      = alerteColor(alertLevel);
  const tooltip    = metric?.tooltip || '';
  const gradId     = 'mp-grad';

  const chartData = points
    .map(p => ({ time: p.created_at, value: parseNumeric(p.value), rawValue: p.value, alerte: parseInt(p.alerte, 10) }))
    .filter(p => p.value !== null);

  return (
    <div
      className={`mp-overlay ${visible ? 'is-visible' : ''}`}
      onClick={onClose}
      role="dialog" aria-modal="true"
    >
      <aside className={`mp-panel ${visible ? 'is-visible' : ''}`} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="mp-header">
          <div>
            <div className="mp-title">{title}</div>
            <div className="mp-subtitle">{serverName}</div>
          </div>
          <button className="mp-close" onClick={onClose} aria-label="Fermer"><X size={14} /></button>
        </div>

        {/* Body */}
        <div className="mp-body">

          {/* Valeur actuelle + alerte */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="mp-row">
              <span>Valeur actuelle</span>
              <strong>{metric?.value ?? 'N/A'}</strong>
            </div>
            <div className={`mp-row mp-alerte-${alertLevel}`}>
              <span>Niveau d'alerte</span>
              <strong>{ALERTE_LABELS[alertLevel]}</strong>
            </div>
          </div>

          {/* Sélecteur de période */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Historique
            </span>
            <TimeRangeSelector value={hours} onChange={setHours} />
          </div>

          {/* Graphique */}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: '0.82rem', padding: '1rem 0' }}>
              <RefreshCw size={14} className="spin" /> Chargement…
            </div>
          )}

          {!loading && chartData.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem', background: '#f8fafc', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
              Aucune donnée pour cette période.
            </div>
          )}

          {!loading && chartData.length > 0 && (
            <div style={{ background: '#f8fafc', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', padding: '1rem 0.5rem 0.5rem' }}>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={color} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="time"
                    tickFormatter={d => new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2}
                    fill={`url(#${gradId})`} dot={false} activeDot={{ r: 4, fill: color, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Tooltip HTML */}
          {tooltip && (
            <div className="mp-desc" dangerouslySetInnerHTML={{ __html: tooltip }} />
          )}

        </div>
      </aside>
    </div>
  );
}

export default MetricPanel;
